// @ts-strict-ignore
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import { pickThumbnailUrl } from '@fc-shared/ui/image-loader/thumbnail-tiers';
import { MediaViewService } from '@fc-shared/ui/media-view-dialog/media-view.service';
import { MediaViewerDialogComponent } from '../media-view-dialog/components/media-viewer.dialog';

// this component is used for displaying a photo models in a dialog
@Component({
  selector: 'fc-image-loader',
  exportAs: 'imageLoader',
  template: `
    <div
      [class.border-radius]="borderRadius"
      [class.hidden]="!imageLoaded || !currentPhoto"
      class="image-container {{ imageForm }}"
      [class.no-click]="notClickable"
      (click)="openPhoto()"
    >
      <img (load)="imageLoadedEvent()" [src]="currentPhoto" alt="photo" />
    </div>
    @if (!!currentPhoto && !imageLoaded) {
      <div class="loader">
        <fc-skeleton
          [shape]="imageLoaderShape"
          width="100%"
          height="100%"
          animation="wave"
        ></fc-skeleton>
      </div>
    }
    @if (!currentPhoto) {
      <fc-image-placeholder
        [thumbWidth]="thumbWidth"
        [thumbHeight]="thumbHeight"
        [imageForm]="imageForm"
        [imagePlaceholder]="imagePlaceholder"
        [placeholderSize]="placeholderSize"
      ></fc-image-placeholder>
    }
  `,
  styles: [
    `
      @use 'var' as *;

      :host {
        display: flex;
        width: 100%;
        height: 100%;
      }

      .loader {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
      }

      .image-loader {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        top: 0;
      }

      .image-container {
        cursor: pointer;
        width: 100%;
        height: 100%;
        background-position: center center;
        background-size: cover;

        &.circle:not(.hidden) {
          display: flex;
        }
      }

      .no-click {
        cursor: default;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .border-radius img {
        border-radius: 4px;
      }

      fc-image-placeholder {
        width: 100%;
        height: 100%;
      }

      .circle {
        border-radius: 100%;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
// todo split this component into base image preview component (99% we just show image preview) and dialog open directive [sc-15780]
export class ImageLoaderComponent {
  private photoModels: AttachmentModel[] = [];

  @Input() notClickable: boolean;

  @Input('thumbWidth') set setThumbWidth(width: number) {
    this.thumbWidth = width;
    this.imageLoaded = false;
  }

  @Input() thumbHeight: number;

  @Input('currentIndex') set setCurrentIndex(index: number) {
    this.currentIndex = index;
  }

  @Input() updateFunction: Function;
  @Input() deleteFunction: Function;
  @Input() updateLoader: boolean;
  @Input() imagePlaceholder = '/assets/icons/vehicle-icon.svg';
  @Input() placeholderSize = 'auto';
  @Input() imageForm: 'auto' | 'circle' = 'auto';
  @Input() borderRadius: boolean = true;
  mediaViewService: MediaViewService = inject(MediaViewService);

  imageLoaded: boolean;
  thumbWidth: number;

  private dialogRef: MatDialogRef<MediaViewerDialogComponent>;
  private subscription: Subscription = new Subscription();

  // todo review PhotoModel usage (do we need it or we can use string url "BOTH")
  @Input('photoModels') set setPhotoModels(photos: AttachmentModel[]) {
    if (this.isCurrentPhotoChanged(photos)) this.imageLoaded = false;
    this.photoModels = photos || [];
    if (this.dialogRef)
      this.dialogRef.componentInstance.data.files = this.photoModels;
  }
  private currentIndex = 0;

  get imageLoaderShape(): 'circle' | 'rectangle' {
    return this.imageForm === 'circle' ? 'circle' : 'rectangle';
  }

  get currentPhoto(): string {
    if (this.photoModels?.length === 0) return;
    const currentPhotoModel = this.photoModels[this.currentIndex];
    return (
      pickThumbnailUrl(
        currentPhotoModel,
        Math.max(this.thumbWidth || 0, this.thumbHeight || 0),
      ) ||
      currentPhotoModel?.scaledImg ||
      currentPhotoModel?.previewImg ||
      currentPhotoModel?.original
    );
  }

  isCurrentPhotoChanged(photos: AttachmentModel[]): boolean {
    if (this.photoModels?.length === 0) return;
    const currentPhotoModel = this.photoModels[this.currentIndex];
    if (!photos) return;
    return (
      (this.photoModels.length > 0 &&
        photos &&
        currentPhotoModel?.scaledImg !==
          photos[this.currentIndex]?.scaledImg) ||
      currentPhotoModel?.previewImg !== photos[this.currentIndex]?.previewImg
    );
  }

  openPhoto(): void {
    if (this.notClickable) return;
    this.dialogRef = this.mediaViewService.openMediaViewDialog({
      user: null,
      currentIndex: this.currentIndex,
      files: this.photoModels,
      updateFunction: this.updateFunction,
      deleteFunction: this.deleteFunction,
      updateLoader: this.updateLoader,
    });
    this.subscription = this.dialogRef.afterClosed().subscribe((data) => {
      if (data && data.action === 'delete') {
        this.deleteFunction(data.photo);
      }
      this.dialogRef = null;
      this.subscription.unsubscribe();
    });
  }

  imageLoadedEvent() {
    this.imageLoaded = true;
  }
}
